import { useMemo, useState } from 'react'

import { FanResponseDto, OfListsService } from '@api'
import { useUnit } from 'effector-react'
import last from 'lodash/last'
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query'

import { $creatorId } from '@/models/creators.ts'
import { handleError } from '@/utils/error'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

import { listsService } from '../service/lists'
import type { ListOrderOptions, ListSortOptions } from '../types'
import {
  listOrderOptions,
  listSortOptions,
} from '../ui/chat-window/lists/consts'

type ListsType = 'all-lists' | 'pinned-lists' | 'all-lists-extended'
interface Options {
  enabled?: boolean
  related_user?: FanResponseDto['id']
}
const ALLOWED_LIST_TYPES = ['custom', 'following']

export const useLists = (key: ListsType, options?: Options) => {
  const creatorId = useUnit($creatorId)
  const [query, setQuery] = useState('')
  const queryClient = useQueryClient()

  const listsQuery = useInfiniteQuery(
    [key, creatorId, query, options?.enabled, options?.related_user],
    ({ pageParam = 0 }) =>
      OfListsService.getLists({
        creatorId: creatorId!,
        offset: 10 * pageParam,
        query,
        relatedUser: options?.related_user,
        filter: key === 'pinned-lists' ? 'chat' : undefined,
        view: key === 'all-lists-extended' ? undefined : 'tiny',
      }),
    {
      getNextPageParam: (page, pages) =>
        page.hasMore ? pages.length : undefined,
      enabled: !!creatorId && options?.enabled,
      refetchInterval: 20000,
    },
  )

  const setSortAndOrderMutation = useMutation(
    ({ sort, order }: { sort: ListSortOptions; order: ListOrderOptions }) =>
      listsService.setSortAndOrder(creatorId!, sort, order),
    {
      onSuccess: () => {
        queryClient.removeQueries('all-lists')
        queryClient.removeQueries('all-lists-extended')
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while updating sort and order')
      },
    },
  )

  const lists = useMemo(
    () =>
      listsQuery.data?.pages
        .flatMap((page) => page.list)
        .filter(isTruthyAndNotEmpty) ?? [],
    [listsQuery.data?.pages],
  )

  const editableLists = useMemo(
    () => lists.filter((list) => ALLOWED_LIST_TYPES.includes(list.type)),
    [lists],
  )

  // @ts-ignore
  const sort: ListSortOptions =
    last(listsQuery.data?.pages)?.sort ??
    setSortAndOrderMutation.variables?.sort ??
    'recent'
  // @ts-ignore
  const order: ListOrderOptions =
    last(listsQuery.data?.pages)?.order ??
    setSortAndOrderMutation.variables?.order ??
    'asc'

  const setSort = (sort: ListSortOptions) => {
    setSortAndOrderMutation.mutate({
      sort,
      order: sort === 'custom' ? 'asc' : order,
    })
  }
  const setOrder = (order: ListOrderOptions) => {
    setSortAndOrderMutation.mutate({ order, sort })
  }

  const isLoading = listsQuery.isLoading && !listsQuery.isFetchingNextPage
  const isFetching = listsQuery.isFetching && !listsQuery.isFetchingNextPage

  const isSortAndOrderDisabled = setSortAndOrderMutation.isLoading || isLoading

  const orderOptions = useMemo(() => {
    if (sort === 'custom') {
      return listOrderOptions.map((option) => ({
        ...option,
        disabled: true,
      }))
    }

    return listOrderOptions
  }, [sort])

  return {
    canLoadMore: Boolean(
      listsQuery.hasNextPage && !listsQuery.isFetchingNextPage,
    ),
    onLoadMore: listsQuery.fetchNextPage,
    lists,
    editableLists,
    isLoading,
    isFetching,
    isFetchingNextPage: listsQuery.isFetchingNextPage,
    sort,
    setSort,
    order,
    setOrder,
    sortOptions: listSortOptions,
    orderOptions,
    query,
    setQuery,
    isSortAndOrderDisabled,
  }
}
